import { useMemo } from 'react';
import { formatDate } from '../../lib/api';
import { memberAdminCodes, useAuth } from '../../lib/auth';
import type { AccessRight, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner } from '../../ui/components';

/**
 * C12 does expose a machine-readable routing table — GET /routing in
 * packages/modules/c12-seyx/src/routes/access.ts — but it's gated by
 * requireService() (a service-to-service `svc` token for the seyx-gateway
 * exchange runtime), not reachable from a human bearer token, so this
 * console cannot call it. This page derives the same "who may call whom"
 * shape honestly from GET /access-rights?status=APPROVED instead (further
 * filtered client-side to unexpired rights, matching /routing's own
 * `validTo: null OR > now` rule) — a clean table, not a fabricated graph.
 */
export function Topology() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isDict = roles.includes('SEYX_ADMIN');
  const myMdas = memberAdminCodes(roles);
  const canSee = isDict || myMdas.length > 0;

  const rightsQ = useApiQuery<Page<AccessRight>>(canSee ? '/api/v1/seyx/access-rights?status=APPROVED&limit=100' : null);

  const byProvider = useMemo(() => {
    const now = Date.now();
    const live = (rightsQ.data?.items ?? []).filter((r) => !r.validTo || new Date(r.validTo).getTime() > now);
    const groups = new Map<string, AccessRight[]>();
    for (const r of live) {
      if (!groups.has(r.providerMember)) groups.set(r.providerMember, []);
      groups.get(r.providerMember)!.push(r);
    }
    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([provider, rows]) => [provider, rows.sort((a, b) => a.consumerMember.localeCompare(b.consumerMember))] as const);
  }, [rightsQ.data]);

  if (!canSee) {
    return (
      <div>
        <PageHeader title="Topology" subtitle="Which members can call which services" />
        <EmptyState title="No access" hint="This screen needs SEYX_ADMIN, or a MEMBER_ADMIN:<code> role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Topology"
        subtitle="Live exchange topology — who may call whom, derived from approved, unexpired access rights (grouped by provider)"
      />
      {rightsQ.error ? (
        <div className="mb-4">
          <ErrorNote message={rightsQ.error} />
        </div>
      ) : rightsQ.loading ? (
        <Spinner />
      ) : byProvider.length === 0 ? (
        <div className="card">
          <EmptyState title="No live exchange routes" hint="Approve an access right to open a route between two members." />
        </div>
      ) : (
        <div className="space-y-5">
          {byProvider.map(([provider, rows]) => (
            <div key={provider}>
              <h2 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700">
                <span className="font-mono">{provider}</span>
                <span className="text-xs font-normal text-slate-400">provides {new Set(rows.map((r) => r.serviceCode)).size} service(s) to {rows.length} consumer link(s)</span>
              </h2>
              <div className="card overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="table-head">Consumer</th>
                      <th className="table-head">Service</th>
                      <th className="table-head">Purpose</th>
                      <th className="table-head">Valid to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="table-cell font-mono text-xs">{r.consumerMember}</td>
                        <td className="table-cell font-mono text-xs">{r.serviceCode}</td>
                        <td className="table-cell max-w-[320px] truncate text-xs" title={r.purpose}>
                          {r.purpose}
                        </td>
                        <td className="table-cell text-slate-500">{formatDate(r.validTo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
